import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Select,
    message,
} from 'antd';
import dayjs from 'dayjs';
import { bdoApi } from '../../../api/client';
import { MATERIAL_OPTIONS } from '../types';
import type { BdoCollectiveEntry } from '../types';

interface Props {
    open: boolean;
    onClose: () => void;
    entry?: BdoCollectiveEntry | null;
    defaultDate?: string;
}

export default function CollectivePackagingModal({
    open,
    onClose,
    entry,
    defaultDate,
}: Props) {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const isEdit = !!entry;

    useEffect(() => {
        if (!open) return;
        if (entry) {
            form.setFieldsValue({
                date: dayjs(entry.date),
                material: entry.material,
                massGrams: Number(entry.massGrams) || 0,
                description: entry.description || '',
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                date: defaultDate ? dayjs(defaultDate) : dayjs(),
                material: 'PAPER',
                massGrams: undefined,
            });
        }
    }, [open, entry, defaultDate, form]);

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['bdo-collective'] });
        queryClient.invalidateQueries({ queryKey: ['bdo-summary'] });
        queryClient.invalidateQueries({ queryKey: ['bdo-sales'] });
    };

    const saveMutation = useMutation({
        mutationFn: (values: any) => {
            const payload = {
                date: values.date.format('YYYY-MM-DD'),
                material: values.material,
                massGrams: Number(values.massGrams),
                description: values.description || undefined,
            };
            return isEdit
                ? bdoApi.updateCollectivePackaging(entry!.id, payload)
                : bdoApi.createCollectivePackaging(payload);
        },
        onSuccess: () => {
            message.success(isEdit ? 'Wpis zaktualizowany' : 'Opakowanie zbiorcze dodane');
            invalidate();
            onClose();
        },
        onError: (error: any) =>
            message.error(
                error?.response?.data?.message || 'Nie udało się zapisać wpisu',
            ),
    });

    return (
        <Modal
            title={isEdit ? 'Edytuj opakowanie zbiorcze' : 'Dodaj opakowanie zbiorcze'}
            open={open}
            onCancel={onClose}
            onOk={() => form.submit()}
            confirmLoading={saveMutation.isPending}
            okText="Zapisz"
            cancelText="Anuluj"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={(values) => saveMutation.mutate(values)}
            >
                <Form.Item
                    name="date"
                    label="Data"
                    rules={[{ required: true, message: 'Wybierz datę' }]}
                >
                    <DatePicker
                        style={{ width: '100%' }}
                        format="DD.MM.YYYY"
                        disabledDate={(current) =>
                            current && current > dayjs().endOf('day')
                        }
                    />
                </Form.Item>
                <Form.Item
                    name="material"
                    label="Materiał"
                    rules={[{ required: true, message: 'Wybierz materiał' }]}
                >
                    <Select options={MATERIAL_OPTIONS} />
                </Form.Item>
                <Form.Item
                    name="massGrams"
                    label="Masa"
                    rules={[{ required: true, message: 'Podaj masę' }]}
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                        addonAfter="g"
                        placeholder="np. 12500"
                    />
                </Form.Item>
                <Form.Item
                    name="description"
                    label="Opis"
                    tooltip="np. karton zbiorczy, taśma, folia stretch, paleta"
                >
                    <Input placeholder="np. karton zbiorczy" maxLength={250} />
                </Form.Item>
            </Form>
        </Modal>
    );
}
